import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import { FiEye, FiRefreshCw } from "react-icons/fi";
import { debounce } from "lodash";
import { useFetchTableData } from "./useFetchTableData";
import "./BrandComponents.css";

const BrandComponents = ({
    header,
    tableTitle,
    apiField,
    addButton,
    createRoute,
    updateRoute,
    viewRoute
}) => {
    const { pointindex } = useParams();
    const navigate = useNavigate();

    const count = [{ value: 5 }, { value: 10 }, { value: 20 }, { value: 50 }];

    const [searchInput, setSearchInput] = useState("");
    const [search, setSearch] = useState(encodeURIComponent(" "));
    const [currentPage, setCurrentPage] = useState(pointindex ? pointindex - 1 : 0);
    const [limit, setLimit] = useState(5);
    const [sort, setSort] = useState("desc");
    const [currentValue, setCurrentValue] = useState("id");
    const [isRefreshing, setIsRefreshing] = useState(false);

    const { data: projectionData, pages, totalEnteries, refetch } = useFetchTableData({
        apiField,
        limit,
        sort,
        currentValue,
        search,
        currentPage
    });

    useEffect(() => {
        const debounced = debounce(() => {
            setSearch(encodeURIComponent(searchInput.trim() || " "));
            setCurrentPage(0);
        }, 300);
        debounced();
        return () => debounced.cancel();
    }, [searchInput]);

    const limitHandler = (e) => {
        setLimit(e.target.value);
        setCurrentPage(0);
    };

    const sortHandler = (value) => {
        setSort(sort === "asc" ? "desc" : "asc");
        setCurrentValue(value);
    };

    const addButtonHandler = () => {
        navigate(createRoute);
    };

    const refreshHandler = () => {
        setIsRefreshing(true);
        refetch();
        setTimeout(() => setIsRefreshing(false), 600);
    };

    // Dedicated view handler - only for view button click
    const handleViewClick = (id) => {
        if (viewRoute) {
            navigate(`${viewRoute}/${id}/${currentPage + 1}`);
        }
    };

    const handleRecentPage = (i) => {
        setCurrentPage(i - 1);
    };

    const previousHandler = () => {
        setCurrentPage(currentPage - 1 < 0 ? 0 : currentPage - 1);
    };

    const nextHandler = () => {
        setCurrentPage(currentPage + 1 > pages - 1 ? pages - 1 : currentPage + 1);
    };

    const renderPagination = () => {
        const totalPages = pages;
        const siblingsCount = 2;
        const firstPage = 1;
        const lastPage = totalPages;
        let pageNumbers = [];

        const startPage = Math.max(firstPage, currentPage + 1 - siblingsCount);
        const endPage = Math.min(lastPage, currentPage + 1 + siblingsCount);

        if (startPage > firstPage + 1) {
            pageNumbers.push(
                <button key={firstPage} className="bc-page-btn" onClick={() => handleRecentPage(firstPage)}>
                    {firstPage}
                </button>
            );
            pageNumbers.push(<span key="dots-start" className="bc-page-dots">...</span>);
        }

        for (let i = startPage; i <= endPage; i++) {
            pageNumbers.push(
                <button
                    key={i}
                    className={`bc-page-btn${i === currentPage + 1 ? ' bc-page-btn--active' : ''}`}
                    onClick={() => handleRecentPage(i)}
                >
                    {i}
                </button>
            );
        }

        if (endPage < lastPage - 1) {
            pageNumbers.push(<span key="dots-end" className="bc-page-dots">...</span>);
            pageNumbers.push(
                <button key={lastPage} className="bc-page-btn" onClick={() => handleRecentPage(lastPage)}>
                    {lastPage}
                </button>
            );
        }

        return pageNumbers;
    };

    return (
        <div className="bc-card">

            {/* ── CONTROLS BAR ── */}
            <div className="bc-toolbar">

                {/* Left: Search */}
                <input
                    className="bc-search"
                    type="text"
                    placeholder="Search..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                />

                {/* Right: Show entries + Refresh + Add */}
                <div className="bc-toolbar__right">
                    <span className="bc-entries__label">Show:</span>
                    <select className="bc-entries__select" onChange={limitHandler} value={limit}>
                        {count.map((item) => (
                            <option key={item.value} value={item.value}>{item.value}</option>
                        ))}
                    </select>

                    <button
                        className={`bc-refresh-btn${isRefreshing ? ' bc-refresh-btn--spinning' : ''}`}
                        onClick={refreshHandler}
                        title="Refresh"
                    >
                        <FiRefreshCw size={14} />
                    </button>

                    {addButton && (
                        <button className="bc-add-btn" onClick={addButtonHandler}>
                            {addButton}
                        </button>
                    )}
                </div>
            </div>

            {/* ── TABLE ── */}
            <div className="bc-table-wrapper">
                <table className="bc-table">
                    <thead>
                        <tr>
                            {header.map((head) => (
                                head.label !== "Action" ? (
                                    <th
                                        key={head.field}
                                        className={`bc-th bc-th--sortable${currentValue === head.field ? ' bc-th--active' : ''}`}
                                        onClick={() => sortHandler(head.field)}
                                    >
                                        {head.label}
                                        {currentValue === head.field && (
                                            <span className="bc-sort-icon">
                                                {sort === 'asc' ? ' ↑' : ' ↓'}
                                            </span>
                                        )}
                                    </th>
                                ) : (
                                    <th key={head.field} className="bc-th">{head.label}</th>
                                )
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {projectionData?.length > 0 ? (
                            projectionData.map((data) => (
                                <tr key={data.id} className="bc-tr">
                                    {header.map((head) => (
                                        <td key={`${data.id}-${head.field}`} className="bc-td">
                                            {head.label === "Action" ? (
                                                <div className="bc-actions">
                                                    {/* Only View button in Action column */}
                                                    <button
                                                        className="bc-action-btn bc-action-btn--view"
                                                        onClick={() => handleViewClick(data.id)}
                                                        title="View"
                                                    >
                                                        <FiEye size={15} />
                                                    </button>
                                                </div>
                                            ) : head.label === "Id" ? (
                                                <div className="bc-id-cell">
                                                    <button
                                                        className="bc-action-btn bc-action-btn--view bc-view-icon-left"
                                                        onClick={() => handleViewClick(data.id)}
                                                        title="View"
                                                    >
                                                        <FiEye size={15} />
                                                    </button>
                                                    <span className="bc-cell-text bc-cell-text--id">
                                                        {data[head.field]}
                                                    </span>
                                                </div>
                                            ) : (
                                                <span className="bc-cell-text">
                                                    {data[head.field]}
                                                </span>
                                            )}
                                        </td>
                                    ))}
                                </tr>
                            ))
                        ) : (
                            <tr className="bc-tr">
                                <td colSpan={header.length} className="bc-td bc-td--empty">
                                    No data found
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* ── PAGINATION ── */}
            <div className="bc-pagination">
                <span className="bc-pagination__info">
                    Showing {projectionData?.length || 0} of {totalEnteries} entries
                </span>

                <div className="bc-pagination__controls">
                    <button
                        className={`bc-nav-btn${currentPage === 0 ? ' bc-nav-btn--disabled' : ''}`}
                        onClick={previousHandler}
                        disabled={currentPage === 0}
                        title="Previous"
                    >
                        <IoIosArrowBack />
                    </button>

                    <div className="bc-pages">
                        {renderPagination()}
                    </div>

                    <button
                        className={`bc-nav-btn${currentPage >= pages - 1 ? ' bc-nav-btn--disabled' : ''}`}
                        onClick={nextHandler}
                        disabled={currentPage >= pages - 1}
                        title="Next"
                    >
                        <IoIosArrowForward />
                    </button>
                </div>
            </div>

        </div>
    );
};

export default BrandComponents;